/**
 * BrownfieldPage
 * Main page for Brownfield Application Analysis — analyze legacy apps for architecture,
 * patterns, principles, features, and documentation gaps.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Building, Puzzle, Ruler, Link, Search, File, BarChart, Clock, Shield, Key, AlertCircle, LayoutDashboard, MessageSquare, Dna, Bot, BookOpen, FlaskConical, Ticket, Network, Blocks, Server, Database, Plug, List, FileQuestion, ShieldCheck, PackageOpen, Zap, TestTube2, Rocket, AlertTriangle, Coins } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { brownfieldApi } from '../../services/api';
import type {
  BrownfieldProject,
  BrownfieldProjectSummary,
  CreateBrownfieldProjectRequest,
  AnalysisDimension,
  AnalysisProgress,

  GitHubAccount,
  GitHubRepoSearchResult,
  DetectedPattern,
  DetectedAntiPattern,
  PrincipleScore,
  FeatureDocumentation,
  DocumentationGap,
} from '../../types';
import styles from './BrownfieldPage.module.css';
import { CodeDNATab, AIArtifactsTab, GeneratedDocsTab, GeneratedTestsTab, TicketProcessingTab } from './CodeIntelligenceTabs';

// ============================================================================
// Sub-components
// ============================================================================

/** Project Card for the listing grid */
const ProjectCard: React.FC<{
  project: BrownfieldProjectSummary;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}> = ({ project, onSelect, onDelete }) => {
  const statusColors: Record<string, string> = {
    created: '#6366f1',
    analyzing: '#f59e0b',
    partial: '#f97316',
    completed: '#10b981',
    failed: '#ef4444',
  };

  return (
    <div className={styles.patternCard} onClick={() => onSelect(project.id)} style={{ cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span className={styles.patternName}>{project.name}</span>
        <span
          style={{
            fontSize: '0.7rem',
            padding: '2px 8px',
            borderRadius: 9999,
            background: statusColors[project.status] + '22',
            color: statusColors[project.status],
            fontWeight: 600,
            textTransform: 'capitalize',
          }}
        >
          {project.status}
        </span>
      </div>
      <p className={styles.patternDescription}>{project.description || 'No description'}</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {project.repositories.map((r) => (
          <span key={r.fullName} style={{ fontSize: '0.7rem', background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 4 }}>
            {r.fullName} {r.language && `· ${r.language}`}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {project.healthScore !== null && (
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: project.healthScore >= 70 ? 'var(--color-success)' : project.healthScore >= 40 ? 'var(--color-warning)' : 'var(--color-error)' }}>
            Health: {project.healthScore}/100
          </span>
        )}
        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
          {project.completedDimensions.length}/15 dimensions
        </span>
        <button
          className={styles.removeRepoBtn}
          onClick={(e) => { e.stopPropagation(); onDelete(project.id); }}
          title="Delete project"
        >
          ×
        </button>
      </div>
    </div>
  );
};

/** Create Project Modal — supports multi-repo selection with Select All & batch add */
const CreateProjectModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreate: (data: CreateBrownfieldProjectRequest) => Promise<void>;
}> = ({ open, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedRepos, setSelectedRepos] = useState<{ fullName: string; provider: 'github'; branch?: string }[]>([]);
  const [orgs, setOrgs] = useState<GitHubAccount[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [repoSearch, setRepoSearch] = useState('');
  const [searchResults, setSearchResults] = useState<GitHubRepoSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      brownfieldApi.getGitHubOrgs().then((res) => {
        if (res.success && res.data) setOrgs(res.data);
      }).catch(() => {});
    }
  }, [open]);

  const handleSearch = useCallback(async () => {
    if (!selectedOrg && !repoSearch) return;
    setSearching(true);
    try {
      const res = await brownfieldApi.searchGitHubRepos(selectedOrg || undefined, repoSearch || undefined);
      if (res.success && res.data) setSearchResults(res.data);
    } catch {
      /* ignore */
    } finally {
      setSearching(false);
    }
  }, [selectedOrg, repoSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedOrg || repoSearch) handleSearch();
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedOrg, repoSearch, handleSearch]);

  // Filtered results: repos not already selected
  const availableResults = searchResults.filter((r) => !selectedRepos.find((s) => s.fullName === r.fullName));

  const addRepo = (repo: GitHubRepoSearchResult) => {
    if (!selectedRepos.find((r) => r.fullName === repo.fullName)) {
      setSelectedRepos((prev) => [...prev, { fullName: repo.fullName, provider: 'github', branch: repo.defaultBranch }]);
    }
  };

  /** Add all visible search results at once */
  const addAllVisible = () => {
    const toAdd = availableResults.slice(0, 30).map((r) => ({
      fullName: r.fullName,
      provider: 'github' as const,
      branch: r.defaultBranch,
    }));
    setSelectedRepos((prev) => {
      const existing = new Set(prev.map((r) => r.fullName));
      return [...prev, ...toAdd.filter((r) => !existing.has(r.fullName))];
    });
  };

  const removeRepo = (fullName: string) => {
    setSelectedRepos((prev) => prev.filter((r) => r.fullName !== fullName));
  };

  /** Clear all selected repos */
  const clearAll = () => setSelectedRepos([]);

  const handleSubmit = async () => {
    if (!name || selectedRepos.length === 0) return;
    setCreating(true);
    try {
      await onCreate({ name, description, repositories: selectedRepos });
      setName('');
      setDescription('');
      setSelectedRepos([]);
      onClose();
    } catch {
      /* error handled upstream */
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.progressOverlay} onClick={onClose}>
      <div className={styles.progressCard} style={{ width: 600, maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: '1.125rem', fontWeight: 600 }}>New Brownfield Project</h2>
        <div className={styles.createForm}>
          <div className={styles.formField}>
            <label>Project Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Legacy Payment System" />
          </div>
          <div className={styles.formField}>
            <label>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of the application" rows={2} />
          </div>

          {/* Selected repos with count badge & clear all */}
          <div className={styles.formField}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ margin: 0 }}>
                Selected Repositories
                {selectedRepos.length > 0 && (
                  <span className={styles.tabBadge} style={{ marginLeft: 8 }}>{selectedRepos.length}</span>
                )}
              </label>
              {selectedRepos.length > 0 && (
                <button
                  onClick={clearAll}
                  style={{
                    fontSize: '0.75rem', color: 'var(--color-error)', background: 'transparent',
                    border: 'none', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
            {selectedRepos.length > 0 ? (
              <div className={styles.selectedRepoGrid}>
                {selectedRepos.map((r) => (
                  <div key={r.fullName} className={styles.selectedRepoChip}>
                    <span className={styles.selectedRepoName}>{r.fullName.split('/')[1]}</span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}>{r.fullName.split('/')[0]}</span>
                    <button className={styles.removeRepoBtn} onClick={() => removeRepo(r.fullName)}>×</button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', margin: '4px 0' }}>
                Search and select repositories below
              </p>
            )}
          </div>

          {/* Repo search */}
          <div className={styles.formField}>
            <label>Search Repositories *</label>
            <div className={styles.repoSearch}>
              <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}>
                <option value="">All accounts</option>
                {orgs.map((o) => (
                  <option key={o.login} value={o.login}>{o.login}</option>
                ))}
              </select>
              <input value={repoSearch} onChange={(e) => setRepoSearch(e.target.value)} placeholder="Search repositories..." />
            </div>
            {(availableResults.length > 0 || searching) && (
              <div className={styles.repoSearchResults}>
                {searching ? (
                  <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Searching...</div>
                ) : (
                  <>
                    {/* Select All bar */}
                    {availableResults.length > 1 && (
                      <div className={styles.selectAllBar} onClick={addAllVisible}>
                        <span>☐ Select all {availableResults.length > 30 ? '(first 30)' : `(${availableResults.length})`}</span>
                      </div>
                    )}
                    {availableResults.slice(0, 30).map((r) => (
                      <div key={r.fullName} className={styles.repoSearchItem} onClick={() => addRepo(r)}>
                        <span className={styles.repoSearchCheck}>+</span>
                        <span className={styles.repoSearchName}>{r.fullName}</span>
                        <span className={styles.repoSearchLang}>{r.language || '—'}</span>
                        {r.stars !== undefined && r.stars > 0 && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginLeft: 4 }}>★{r.stars}</span>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className={styles.backButton} onClick={onClose}>Cancel</button>
            <button className={styles.createButton} disabled={!name || selectedRepos.length === 0 || creating} onClick={handleSubmit}>
              {creating ? 'Creating...' : `Create Project (${selectedRepos.length} repo${selectedRepos.length !== 1 ? 's' : ''})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Analysis Progress Panel (SSE-driven) — shows dimension completion counts & elapsed time */
const AnalysisProgressPanel: React.FC<{
  projectId: string;
  onComplete: () => void;
  onError: (msg: string) => void;
}> = ({ projectId, onComplete, onError }) => {
  const [progress, setProgress] = useState<AnalysisProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  // Elapsed time ticker
  useEffect(() => {
    if (!running || !startTime) return;
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [running, startTime]);

  const startAnalysis = useCallback(async () => {
    setRunning(true);
    setStartTime(Date.now());
    setElapsed(0);
    await brownfieldApi.triggerAnalysisFetch(
      projectId,
      undefined,
      (data) => {
        if (data.progress) setProgress(data.progress);
      },
      () => {
        setRunning(false);
        onComplete();
      },
      (err) => {
        setRunning(false);
        onError(err);
      },
    );
  }, [projectId, onComplete, onError]);

  if (!running && !progress) {
    return (
      <div style={{ marginBottom: 24 }}>
        <button className={styles.createButton} onClick={startAnalysis}>
          ▶ Run Full Analysis
        </button>
      </div>
    );
  }

  const pct = progress?.percentage ?? 0;
  const steps = progress?.steps ?? [];
  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const totalCount = steps.length || 15;
  const failedCount = steps.filter((s) => s.status === 'failed').length;

  const dimensionIcons: Record<string, React.ReactNode> = {
    'architecture': <Network size={20} className={styles.sectionIcon} />,
    'design-patterns': <Blocks size={20} className={styles.sectionIcon} />,
    'code-principles': <List size={20} className={styles.sectionIcon} />,
    'features': <Zap size={20} className={styles.sectionIcon} />,
    'microservice-map': <Server size={20} className={styles.sectionIcon} />,
    'documentation-gaps': '📝',
    'security': <ShieldCheck size={20} className={styles.sectionIcon} />,
    'dependencies': <PackageOpen size={20} className={styles.sectionIcon} />,
    'performance': <Zap size={20} className={styles.sectionIcon} />,
    'test-coverage': <FlaskConical size={20} className={styles.sectionIcon} />,
    'api-contracts': <Plug size={20} className={styles.sectionIcon} />,
    'database': <Database size={20} className={styles.sectionIcon} />,
    'devops-maturity': '🚀',
    'error-handling': '🛡️',
    'tech-debt': '💰',
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div className={styles.progressOverlay}>
      <div className={styles.progressCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className={styles.progressTitle} style={{ margin: 0 }}>
            {running ? 'Analyzing Codebase...' : 'Analysis Complete'}
          </h3>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
            ⏱ {formatTime(elapsed)}
          </span>
        </div>

        {/* Completion counter */}
        <div className={styles.progressCounter}>
          <span className={styles.progressCountDone}>{completedCount}</span>
          <span className={styles.progressCountSep}>/</span>
          <span className={styles.progressCountTotal}>{totalCount}</span>
          <span className={styles.progressCountLabel}>dimensions completed</span>
          {failedCount > 0 && (
            <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--color-error)' }}>
              ({failedCount} failed)
            </span>
          )}
        </div>

        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
          {progress?.currentStep || 'Initializing...'}
        </p>
        <ul className={styles.progressSteps}>
          {steps.map((step, i) => (
            <li
              key={i}
              className={`${styles.progressStep} ${step.status === 'completed' ? styles.stepDone : step.status === 'running' ? styles.stepActive : step.status === 'failed' ? styles.stepFailed : ''}`}
            >
              <span style={{ width: 22, textAlign: 'center' }}>
                {step.status === 'completed' ? '✓' : step.status === 'running' ? '⟳' : step.status === 'failed' ? '✗' : '○'}
              </span>
              <span>{dimensionIcons[step.name] || '📊'}</span>
              <span style={{ flex: 1, textTransform: 'capitalize' }}>{step.name.replace(/-/g, ' ')}</span>
              {step.status === 'completed' && (
                <span style={{ fontSize: '0.7rem', color: 'var(--color-success)' }}>Done</span>
              )}
              {step.status === 'running' && (
                <span className={styles.pulseIndicator} />
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// Tab Components
// ============================================================================

/** Overview Tab */
const OverviewTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const arch = project.architectureAnalysis;
  const docs = project.documentationGaps;

  return (
    <div>
      {/* Health Score */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Project Health</h3>
        <div className={styles.healthScoreContainer}>
          <div className={`${styles.healthScore} ${
            (project.healthScore ?? 0) >= 70 ? styles.healthGood : (project.healthScore ?? 0) >= 40 ? styles.healthWarning : styles.healthBad
          }`}>
            {project.healthScore ?? '—'}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>
              {(project.healthScore ?? 0) >= 70 ? 'Healthy' : (project.healthScore ?? 0) >= 40 ? 'Needs Attention' : 'Critical'}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {project.completedDimensions.length}/15 analysis dimensions completed
            </div>
          </div>
        </div>
      </div>

      {/* Architecture Quick Summary */}
      {arch && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Architecture: {arch.pattern}</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{arch.description}</p>
          <div className={styles.scoreGrid} style={{ marginTop: 12 }}>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Coupling</div>
              <div className={styles.scoreValue} style={{ color: arch.couplingScore <= 40 ? 'var(--color-success)' : arch.couplingScore <= 70 ? 'var(--color-warning)' : 'var(--color-error)' }}>
                {arch.couplingScore}<span style={{ fontSize: '0.75rem' }}>/100</span>
              </div>
            </div>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Cohesion</div>
              <div className={styles.scoreValue} style={{ color: arch.cohesionScore >= 60 ? 'var(--color-success)' : arch.cohesionScore >= 40 ? 'var(--color-warning)' : 'var(--color-error)' }}>
                {arch.cohesionScore}<span style={{ fontSize: '0.75rem' }}>/100</span>
              </div>
            </div>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Confidence</div>
              <div className={styles.scoreValue} style={{ color: 'var(--primary-500)' }}>
                {Math.round(arch.patternConfidence)}<span style={{ fontSize: '0.75rem' }}>%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Repos */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Repositories ({project.repositories.length})</h3>
        <div className={styles.scoreGrid}>
          {project.repositories.map((r) => (
            <div key={r.id} className={styles.scoreCard}>
              <div className={styles.scoreName}>{r.fullName}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {r.language || 'Unknown'} · {r.fileCount} files
              </div>
              {r.techStack && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                  {r.techStack.framework && `${r.techStack.framework} · `}
                  {r.techStack.runtime && `${r.techStack.runtime} · `}
                  {r.techStack.database && r.techStack.database}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Documentation Score */}
      {docs && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Documentation Coverage</h3>
          <div className={styles.scoreGrid}>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Overall</div>
              <div className={styles.scoreValue}>{docs.overallScore}<span style={{ fontSize: '0.75rem' }}>/100</span></div>
            </div>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>JSDoc</div>
              <div className={styles.scoreValue}>{docs.jsdocCoverage}<span style={{ fontSize: '0.75rem' }}>%</span></div>
            </div>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Comment Density</div>
              <div className={styles.scoreValue}>{docs.commentCoverage}<span style={{ fontSize: '0.75rem' }}>%</span></div>
            </div>
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>README Quality</div>
              <div className={styles.scoreValue} style={{ fontSize: '1rem', textTransform: 'capitalize' }}>{docs.readmeQuality}</div>
            </div>
          </div>
        </div>
      )}

      {/* RAG Index Status */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Knowledge Index (RAG)</h3>
        <div className={styles.scoreGrid}>
          <div className={styles.scoreCard}>
            <div className={styles.scoreName}>Status</div>
            <div className={styles.scoreValue} style={{
              fontSize: '0.9rem',
              color: project.ragIndexed ? 'var(--color-success)' : 'var(--text-tertiary)',
            }}>
              {project.ragIndexed ? '✓ Indexed' : '○ Not Indexed'}
            </div>
          </div>
          <div className={styles.scoreCard}>
            <div className={styles.scoreName}>Vectors Stored</div>
            <div className={styles.scoreValue}>{project.vectorCount ?? 0}</div>
          </div>
          <div className={styles.scoreCard}>
            <div className={styles.scoreName}>Ask Codebase</div>
            <div className={styles.scoreValue} style={{ fontSize: '0.85rem' }}>
              {project.ragIndexed ? 'Semantic Search' : 'Summary Only'}
            </div>
          </div>
        </div>
        {!project.ragIndexed && (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: 8 }}>
            Run analysis to index source code into the vector store for semantic search.
          </p>
        )}
      </div>
    </div>
  );
};

/** Architecture Tab */
const ArchitectureTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const arch = project.architectureAnalysis;
  if (!arch) return <div className={styles.emptyState}><p className={styles.emptyTitle}>Architecture analysis not yet run</p></div>;

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Architecture Pattern: {arch.pattern}</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>{arch.description}</p>

        {/* Diagram */}
        {arch.mermaidDiagram && (
          <div className={styles.diagramContainer}>
            <div className={styles.scoreName} style={{ marginBottom: 8 }}>C4 Architecture Diagram</div>
            <pre className={styles.diagramCode}>{arch.mermaidDiagram}</pre>
          </div>
        )}
      </div>

      {/* Layers */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Layers</h3>
        <div className={styles.patternGrid}>
          {arch.layers.map((layer, i) => (
            <div key={i} className={styles.patternCard}>
              <div className={styles.patternName}>{layer.name}</div>
              <div className={styles.patternCategory}>{layer.responsibility}</div>
              <p className={styles.patternDescription}>{layer.description}</p>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                {layer.fileCount} files · {layer.directories.slice(0, 3).join(', ')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strengths & Concerns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Strengths</h3>
          <ul className={styles.recommendationList}>
            {arch.strengths.map((s, i) => <li key={i} className={styles.recommendationItem}>{s}</li>)}
          </ul>
        </div>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Concerns</h3>
          <ul className={styles.recommendationList}>
            {arch.concerns.map((c, i) => <li key={i} className={styles.recommendationItem}>{c}</li>)}
          </ul>
        </div>
      </div>

      {/* Recommendations */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Recommendations</h3>
        <ul className={styles.recommendationList}>
          {arch.recommendations.map((r, i) => <li key={i} className={styles.recommendationItem}>{r}</li>)}
        </ul>
      </div>
    </div>
  );
};

/** Patterns & Principles Tab */
const PatternsTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const dp = project.designPatterns;
  const cp = project.codePrinciples;

  return (
    <div>
      {/* Design Patterns */}
      {dp && (
        <>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Design Patterns
              <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 400, textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                Maturity: {dp.overallMaturity}
              </span>
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>{dp.summary}</p>
            <div className={styles.patternGrid}>
              {dp.patterns.map((p: DetectedPattern, i: number) => (
                <div key={i} className={styles.patternCard}>
                  <div className={styles.patternName}>{p.name}</div>
                  <div className={styles.patternCategory}>{p.category}</div>
                  <p className={styles.patternDescription}>{p.description}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                    <span>{p.occurrences} occurrence{p.occurrences !== 1 && 's'}</span>
                    <span style={{ textTransform: 'capitalize' }}>Quality: {p.implementationQuality}</span>
                  </div>
                  <div className={styles.confidenceBar}>
                    <div className={styles.confidenceFill} style={{ width: `${p.confidence}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Anti-Patterns */}
          {dp.antiPatterns.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Anti-Patterns Detected</h3>
              {dp.antiPatterns.map((ap: DetectedAntiPattern, i: number) => (
                <div key={i} className={styles.antiPatternCard}>
                  <div className={styles.antiPatternName}>
                    {ap.name}
                    <span style={{ marginLeft: 8, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                      {ap.severity}
                    </span>
                  </div>
                  <p className={styles.antiPatternDescription}>{ap.description}</p>
                  <div style={{ fontSize: '0.7rem', color: '#991b1b', marginTop: 4 }}>
                    💡 {ap.recommendation}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Code Principles */}
      {cp && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Code Principles
            <span style={{ marginLeft: 8, fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary-500)' }}>
              {cp.overallScore}/100
            </span>
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>{cp.summary}</p>

          <div className={styles.scoreGrid}>
            {cp.principles.map((p: PrincipleScore, i: number) => {
              const gradeClass = p.grade === 'A' ? styles.gradeA : p.grade === 'B' ? styles.gradeB : p.grade === 'C' ? styles.gradeC : p.grade === 'D' ? styles.gradeD : styles.gradeF;
              return (
                <div key={i} className={styles.scoreCard}>
                  <div className={styles.scoreName}>{p.acronym} — {p.name}</div>
                  <div className={styles.scoreValue}>
                    {p.score}
                    <span className={`${styles.scoreGrade} ${gradeClass}`}>{p.grade}</span>
                  </div>
                  <p style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>{p.description}</p>
                </div>
              );
            })}
          </div>

          {cp.topImprovements.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Top Improvements</h4>
              <ul className={styles.recommendationList}>
                {cp.topImprovements.map((t, i) => <li key={i} className={styles.recommendationItem}>{t}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {!dp && !cp && (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>Patterns & Principles not yet analyzed</p>
          <p className={styles.emptyDescription}>Run the analysis to detect design patterns and evaluate code principles.</p>
        </div>
      )}
    </div>
  );
};

/** Features Tab */
const FeaturesTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const features = project.features || [];

  if (features.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyTitle}>No features extracted yet</p>
        <p className={styles.emptyDescription}>Run the analysis to auto-detect features and generate per-feature documentation.</p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Discovered Features ({features.length})</h3>
        {features.map((f: FeatureDocumentation) => (
          <div key={f.id} className={styles.featureItem}>
            <div className={styles.featureHeader} onClick={() => setOpenId(openId === f.id ? null : f.id)}>
              <div>
                <span className={styles.featureName}>{f.name}</span>
                <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  {f.involvedFiles.length} files
                </span>
              </div>
              <span className={`${styles.featureComplexity} ${f.complexity === 'low' ? styles.complexityLow : f.complexity === 'medium' ? styles.complexityMedium : styles.complexityHigh}`}>
                {f.complexity}
              </span>
            </div>
            {openId === f.id && (
              <div className={styles.featureBody}>
                <h4>Purpose</h4>
                <p>{f.purpose}</p>

                <h4>Description</h4>
                <p>{f.description}</p>

                {f.entryPoints.length > 0 && (
                  <>
                    <h4>Entry Points</h4>
                    <ul>
                      {f.entryPoints.map((ep, i) => (
                        <li key={i}>
                          <strong>{ep.type}</strong>: {ep.method ? `${ep.method} ` : ''}{ep.path} → {ep.file}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {f.dataFlow && (
                  <>
                    <h4>Data Flow</h4>
                    <p>{f.dataFlow}</p>
                  </>
                )}

                {f.sequenceDiagram && (
                  <>
                    <h4>Sequence Diagram</h4>
                    <div className={styles.diagramContainer}>
                      <pre className={styles.diagramCode}>{f.sequenceDiagram}</pre>
                    </div>
                  </>
                )}

                <h4>Test Coverage</h4>
                <p>
                  {f.testCoverage.hasTests ? `${f.testCoverage.estimatedCoverage}% estimated coverage` : 'No tests found'}
                  {f.testCoverage.missingTestAreas.length > 0 && ` · Missing: ${f.testCoverage.missingTestAreas.join(', ')}`}
                </p>

                <h4>Generated Documentation</h4>
                <div style={{ whiteSpace: 'pre-wrap' }}>{f.documentation}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/** Microservices Tab */
const MicroservicesTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const map = project.microserviceMap;
  if (!map) return <div className={styles.emptyState}><p className={styles.emptyTitle}>Microservice map not yet generated</p></div>;

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Service Topology</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>{map.summary}</p>
        {map.topologyDiagram && (
          <div className={styles.diagramContainer}>
            <pre className={styles.diagramCode}>{map.topologyDiagram}</pre>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Services ({map.services.length})</h3>
        <div className={styles.patternGrid}>
          {map.services.map((s) => (
            <div key={s.id} className={styles.patternCard}>
              <div className={styles.patternName}>{s.name}</div>
              <div className={styles.patternCategory}>{s.type} · {s.techStack}</div>
              <p className={styles.patternDescription}>{s.description}</p>
              {s.port && <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>Port: {s.port}</div>}
            </div>
          ))}
        </div>
      </div>

      {map.infrastructure.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Infrastructure</h3>
          <div className={styles.patternGrid}>
            {map.infrastructure.map((infra, i) => (
              <div key={i} className={styles.patternCard}>
                <div className={styles.patternName}>{infra.name}</div>
                <div className={styles.patternCategory}>{infra.type} · {infra.technology}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                  Connected to: {infra.connectedServices.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Documentation Gaps Tab */
const DocsGapsTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const docs = project.documentationGaps;
  if (!docs) return <div className={styles.emptyState}><p className={styles.emptyTitle}>Documentation gap analysis not yet run</p></div>;

  const severityColor = (s: string) =>
    s === 'critical' ? '#ef4444' : s === 'high' ? '#f97316' : s === 'medium' ? '#f59e0b' : '#22c55e';

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Documentation Gaps ({docs.gaps.length})</h3>
        <div className={styles.gapList}>
          {docs.gaps.map((g: DocumentationGap, i: number) => (
            <div key={i} className={styles.gapCard}>
              <div className={styles.gapSeverityDot} style={{ background: severityColor(g.severity) }} />
              <div>
                <div className={styles.gapArea}>{g.area}</div>
                <p className={styles.gapDescription}>{g.description}</p>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {g.affectedFiles.slice(0, 3).join(', ')}
                  {g.affectedFiles.length > 3 && ` +${g.affectedFiles.length - 3} more`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Recommendations</h3>
        <ul className={styles.recommendationList}>
          {docs.recommendations.map((r, i) => <li key={i} className={styles.recommendationItem}>{r}</li>)}
        </ul>
      </div>
    </div>
  );
};

/** Ask Tab — RAG-powered codebase Q&A */
const AskTab: React.FC<{ project: BrownfieldProject }> = ({ project }) => {
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState<Array<{ file: string; snippet: string; relevance: number }>>([]);
  const [loading, setLoading] = useState(false);

  const isRagReady = project.ragIndexed && (project.vectorCount ?? 0) > 0;

  const handleAsk = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setAnswer('');
    setSources([]);
    try {
      const res = await brownfieldApi.askCodebase(project.id, query.trim());
      if (res.success && res.data) {
        setAnswer(res.data.answer);
        setSources(res.data.sources || []);
      }
    } catch {
      setAnswer('Failed to get response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    'What is the overall architecture of this codebase?',
    'How does the authentication flow work?',
    'What design patterns are used and where?',
    'What are the main API endpoints?',
    'Which components have the highest coupling?',
    'Where are the biggest technical debt areas?',
  ];

  return (
    <div className={styles.askContainer}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Ask About This Codebase</h3>

        {/* RAG Status Indicator */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          padding: '8px 12px', borderRadius: 'var(--radius-md)',
          background: isRagReady ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
          border: `1px solid ${isRagReady ? 'var(--color-success)' : 'var(--color-warning)'}`,
          fontSize: '0.8rem',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: isRagReady ? 'var(--color-success)' : 'var(--color-warning)',
          }} />
          {isRagReady ? (
            <span>
              <strong>RAG Indexed</strong> — {project.vectorCount} vectors from source code &amp; analysis.
              Answers are grounded in real code via semantic search.
            </span>
          ) : (
            <span>
              <strong>Not Indexed</strong> — Run analysis first to index the codebase.
              Answers will use analysis summaries only.
            </span>
          )}
        </div>

        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
          Ask questions about architecture, patterns, features, or any aspect of the analyzed codebase.
        </p>
        <div className={styles.askInput}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. How does the authentication flow work?"
            onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
          />
          <button className={styles.createButton} onClick={handleAsk} disabled={loading || !query.trim()}>
            {loading ? 'Thinking...' : 'Ask'}
          </button>
        </div>

        {/* Suggested questions */}
        {!answer && !loading && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {suggestedQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => { setQuery(q); }}
                style={{
                  fontSize: '0.75rem', padding: '4px 10px', borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
                  cursor: 'pointer', color: 'var(--text-secondary)',
                  transition: 'var(--transition-base)',
                }}
                onMouseOver={(e) => (e.currentTarget.style.borderColor = 'var(--primary-500)')}
                onMouseOut={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {answer && (
          <div className={styles.askAnswer}>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.6 }}>{answer}</pre>
          </div>
        )}

        {sources.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              📄 Sources ({sources.length} relevant chunks)
            </h4>
            {sources.map((s, i) => (
              <div key={i} style={{
                fontSize: '0.8rem', padding: '10px 12px', marginBottom: 8,
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)',
                background: 'var(--bg-secondary)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <strong style={{ fontFamily: 'var(--font-code)', fontSize: '0.75rem' }}>{s.file}</strong>
                  <span style={{
                    fontSize: '0.7rem', padding: '2px 8px', borderRadius: 'var(--radius-full)',
                    background: s.relevance > 0.8 ? 'rgba(16, 185, 129, 0.15)' : s.relevance > 0.6 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                    color: s.relevance > 0.8 ? 'var(--color-success)' : s.relevance > 0.6 ? 'var(--color-warning)' : 'var(--text-tertiary)',
                  }}>
                    {Math.round(s.relevance * 100)}% match
                  </span>
                </div>
                <pre style={{
                  fontSize: '0.7rem', margin: 0, whiteSpace: 'pre-wrap',
                  color: 'var(--text-secondary)', fontFamily: 'var(--font-code)',
                  maxHeight: 120, overflow: 'auto',
                }}>{s.snippet}</pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Generic Dimension Tab — Renders any of the 9 new analysis dimensions
// ============================================================================

const DIMENSION_CONFIG: Record<string, {
  title: string;
  dataKey: keyof BrownfieldProject;
  icon: React.ReactNode;
}> = {
    'security': { title: 'Security & Vulnerability Analysis', dataKey: 'securityAnalysis', icon: <ShieldCheck size={20} className={styles.sectionIcon} /> },
    'dependencies': { title: 'Dependency & Supply Chain Analysis', dataKey: 'dependencyAnalysis', icon: <PackageOpen size={20} className={styles.sectionIcon} /> },
    'performance': { title: 'Performance & Scalability Analysis', dataKey: 'performanceAnalysis', icon: <Zap size={20} className={styles.sectionIcon} /> },
    'test-coverage': { title: 'Test Coverage & Quality Analysis', dataKey: 'testCoverageAnalysis', icon: <FlaskConical size={20} className={styles.sectionIcon} /> },
    'api-contracts': { title: 'API Contract Analysis', dataKey: 'apiContractAnalysis', icon: <Plug size={20} className={styles.sectionIcon} /> },
    'database': { title: 'Database & Data Model Analysis', dataKey: 'databaseAnalysis', icon: <Database size={20} className={styles.sectionIcon} /> },
    'devops-maturity': { title: 'DevOps & CI/CD Maturity', dataKey: 'devopsMaturity', icon: <Rocket size={20} className={styles.sectionIcon} /> },
    'error-handling': { title: 'Error Handling & Observability', dataKey: 'errorHandlingAnalysis', icon: <AlertTriangle size={20} className={styles.sectionIcon} /> },
    'tech-debt': { title: 'Tech Debt Estimation', dataKey: 'techDebtAnalysis', icon: <Coins size={20} className={styles.sectionIcon} /> },
  };

  /** Severity badge helper */
  const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
    const colorMap: Record<string, string> = {
      low: '#10b981', medium: '#f59e0b', high: '#f97316', critical: '#ef4444',
    };
    return (
    <span style={{
      fontSize: '0.65rem', padding: '2px 6px', borderRadius: 9999, fontWeight: 600,
      background: (colorMap[severity] || '#6366f1') + '18',
      color: colorMap[severity] || '#6366f1',
      textTransform: 'capitalize',
    }}>
      {severity}
    </span>
  );
};

/** Score gauge mini-component */
const ScoreGauge: React.FC<{ score: number; label: string }> = ({ score, label }) => (
  <div className={styles.scoreCard}>
    <div className={styles.scoreName}>{label}</div>
    <div className={styles.scoreValue} style={{
      color: score >= 70 ? 'var(--color-success)' : score >= 40 ? 'var(--color-warning)' : 'var(--color-error)',
    }}>
      {score}<span style={{ fontSize: '0.75rem' }}>/100</span>
    </div>
  </div>
);

/** Renders a list of items with severity badges */
const IssueList: React.FC<{ items: any[]; nameKey?: string; severityKey?: string; descKey?: string }> = ({
  items, nameKey = 'type', severityKey = 'severity', descKey = 'description',
}) => (
  <div className={styles.patternGrid}>
    {items.map((item, i) => (
      <div key={i} className={styles.patternCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span className={styles.patternName}>{item[nameKey] || item.title || item.name || `Item ${i + 1}`}</span>
          {item[severityKey] && <SeverityBadge severity={item[severityKey]} />}
        </div>
        <p className={styles.patternDescription}>{item[descKey] || ''}</p>
        {item.recommendation && (
          <div style={{ fontSize: '0.7rem', color: 'var(--primary-500)', marginTop: 4 }}>
            💡 {item.recommendation}
          </div>
        )}
        {item.affectedFiles?.length > 0 && (
          <div style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
            Files: {item.affectedFiles.slice(0, 3).join(', ')}{item.affectedFiles.length > 3 ? ` +${item.affectedFiles.length - 3} more` : ''}
          </div>
        )}
      </div>
    ))}
  </div>
);

/** Recommendation list */
const RecommendationsList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className={styles.recommendationList}>
    {items.map((r, i) => <li key={i} className={styles.recommendationItem}>{r}</li>)}
  </ul>
);

/** Boolean status indicator */
const BoolStatus: React.FC<{ value: boolean; label: string }> = ({ value, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', padding: '4px 0' }}>
    <span style={{ color: value ? 'var(--color-success)' : 'var(--color-error)' }}>{value ? '✓' : '✗'}</span>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
  </div>
);

const GenericDimensionTab: React.FC<{ project: BrownfieldProject; dimension: string }> = ({ project, dimension }) => {
  const config = DIMENSION_CONFIG[dimension];
  if (!config) return <div className={styles.emptyState}><p className={styles.emptyTitle}>Unknown dimension</p></div>;

  const data = project[config.dataKey] as any;
  if (!data) return (
    <div className={styles.emptyState}>
      <p className={styles.emptyTitle}>{config.icon} {config.title}</p>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>Analysis not yet run. Trigger analysis to generate this report.</p>
    </div>
  );

  return (
    <div>
      {/* Header + Score */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{config.icon} {config.title}</h3>
        {data.summary && <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{data.summary}</p>}

        <div className={styles.scoreGrid} style={{ marginTop: 12 }}>
          {data.overallScore != null && <ScoreGauge score={data.overallScore} label="Overall Score" />}
          {data.riskLevel && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Risk Level</div>
              <div className={styles.scoreValue} style={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>{data.riskLevel}</div>
            </div>
          )}
          {data.totalDependencies != null && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Total Deps</div>
              <div className={styles.scoreValue}>{data.totalDependencies}</div>
            </div>
          )}
          {data.tryCatchCoverage != null && <ScoreGauge score={data.tryCatchCoverage} label="Try/Catch Coverage" />}
          {data.totalDebtDays != null && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Tech Debt</div>
              <div className={styles.scoreValue}>{data.totalDebtDays}<span style={{ fontSize: '0.7rem' }}> dev-days</span></div>
            </div>
          )}
          {data.debtCategory && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Debt Level</div>
              <div className={styles.scoreValue} style={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>{data.debtCategory}</div>
            </div>
          )}
          {data.maturityLevel && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Maturity</div>
              <div className={styles.scoreValue} style={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>{data.maturityLevel}</div>
            </div>
          )}
          {data.testFramework && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>Test Framework</div>
              <div className={styles.scoreValue} style={{ fontSize: '0.85rem' }}>{data.testFramework}</div>
            </div>
          )}
          {data.versioningStrategy && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>API Versioning</div>
              <div className={styles.scoreValue} style={{ fontSize: '0.85rem' }}>{data.versioningStrategy}</div>
            </div>
          )}
          {data.hasOpenApiSpec != null && (
            <div className={styles.scoreCard}>
              <div className={styles.scoreName}>OpenAPI Spec</div>
              <div className={styles.scoreValue} style={{ color: data.hasOpenApiSpec ? 'var(--color-success)' : 'var(--text-tertiary)' }}>
                {data.hasOpenApiSpec ? '✓ Yes' : '✗ No'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security-specific: Auth Assessment */}
      {data.authAssessment && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Authentication & Authorization</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
            <BoolStatus value={data.authAssessment.hasAuthentication} label={`Auth: ${data.authAssessment.authType || 'None'}`} />
            <BoolStatus value={data.authAssessment.hasAuthorization} label="Authorization" />
            <BoolStatus value={data.authAssessment.hasRateLimiting} label="Rate Limiting" />
            <BoolStatus value={data.authAssessment.hasCsrfProtection} label="CSRF Protection" />
            <BoolStatus value={data.authAssessment.hasInputValidation} label="Input Validation" />
            <BoolStatus value={data.authAssessment.hasHelmetOrSecurityHeaders} label="Security Headers" />
          </div>
        </div>
      )}

      {/* Dependency-specific: Version Pinning */}
      {data.versionPinning && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Version Pinning</h3>
          <div className={styles.scoreGrid}>
            <div className={styles.scoreCard}><div className={styles.scoreName}>Pinned</div><div className={styles.scoreValue}>{data.versionPinning.pinnedCount}</div></div>
            <div className={styles.scoreCard}><div className={styles.scoreName}>Unpinned</div><div className={styles.scoreValue}>{data.versionPinning.unpinnedCount}</div></div>
            <BoolStatus value={data.versionPinning.usesLockFile} label={`Lock: ${data.versionPinning.lockFileType}`} />
          </div>
        </div>
      )}

      {/* Test-specific: Test Pyramid */}
      {data.testPyramid && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Test Pyramid</h3>
          <div className={styles.scoreGrid}>
            <div className={styles.scoreCard}><div className={styles.scoreName}>Unit</div><div className={styles.scoreValue}>{data.testPyramid.unitTests}</div></div>
            <div className={styles.scoreCard}><div className={styles.scoreName}>Integration</div><div className={styles.scoreValue}>{data.testPyramid.integrationTests}</div></div>
            <div className={styles.scoreCard}><div className={styles.scoreName}>E2E</div><div className={styles.scoreValue}>{data.testPyramid.e2eTests}</div></div>
            <div className={styles.scoreCard}><div className={styles.scoreName}>Balance</div><div className={styles.scoreValue} style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>{data.testPyramid.balance}</div></div>
          </div>
        </div>
      )}

      {/* Coverage metrics */}
      {data.coverageMetrics && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Coverage Metrics</h3>
          <div className={styles.scoreGrid}>
            <ScoreGauge score={data.coverageMetrics.estimatedLineCoverage} label="Line Coverage" />
            <ScoreGauge score={data.coverageMetrics.estimatedBranchCoverage} label="Branch Coverage" />
            <div className={styles.scoreCard}><div className={styles.scoreName}>Tested Files</div><div className={styles.scoreValue}>{data.coverageMetrics.testedFiles}</div></div>
            <div className={styles.scoreCard}><div className={styles.scoreName}>Untested</div><div className={styles.scoreValue} style={{ color: 'var(--color-error)' }}>{data.coverageMetrics.untestedFiles}</div></div>
          </div>
        </div>
      )}

      {/* DevOps-specific: CI/CD */}
      {data.cicd && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>CI/CD Pipeline</h3>
          <BoolStatus value={data.cicd.hasPipeline} label={`Platform: ${data.cicd.platform || 'None'}`} />
          {data.cicd.stages?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {data.cicd.stages.map((s: string, i: number) => (
                <span key={i} style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: 'var(--primary-500)18', color: 'var(--primary-500)' }}>{s}</span>
              ))}
            </div>
          )}
          <BoolStatus value={data.cicd.hasAutomatedTests} label="Automated Tests" />
          <BoolStatus value={data.cicd.hasAutomatedDeploy} label="Automated Deploy" />
        </div>
      )}

      {/* DevOps: Containerization */}
      {data.containerization && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Containerization</h3>
          <BoolStatus value={data.containerization.hasDockerfile} label="Dockerfile" />
          <BoolStatus value={data.containerization.hasCompose} label="Docker Compose" />
          <BoolStatus value={data.containerization.hasKubernetes} label="Kubernetes" />
          <BoolStatus value={data.containerization.multiStage} label="Multi-stage Build" />
        </div>
      )}

      {/* DevOps: Monitoring */}
      {data.monitoring && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Monitoring & Observability</h3>
          <BoolStatus value={data.monitoring.hasLogging} label={`Logging: ${data.monitoring.loggingFramework || 'None'}`} />
          <BoolStatus value={data.monitoring.hasMetrics} label="Metrics" />
          <BoolStatus value={data.monitoring.hasAlerting} label="Alerting" />
          <BoolStatus value={data.monitoring.hasHealthChecks} label="Health Checks" />
          <BoolStatus value={data.monitoring.hasTracing} label="Distributed Tracing" />
        </div>
      )}

      {/* Error Handling: Logging Quality */}
      {data.loggingAssessment && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Logging Quality</h3>
          <BoolStatus value={data.loggingAssessment.hasStructuredLogging} label={`Framework: ${data.loggingAssessment.loggingFramework}`} />
          <BoolStatus value={!data.loggingAssessment.sensitiveDataExposure} label="No Sensitive Data Exposure" />
        </div>
      )}

      {/* Error Handling: Resilience */}
      {data.gracefulDegradation && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Resilience Patterns</h3>
          <BoolStatus value={data.gracefulDegradation.hasCircuitBreakers} label="Circuit Breakers" />
          <BoolStatus value={data.gracefulDegradation.hasRetryLogic} label="Retry Logic" />
          <BoolStatus value={data.gracefulDegradation.hasFallbacks} label="Fallbacks" />
          <BoolStatus value={data.gracefulDegradation.hasTimeouts} label="Timeouts" />
        </div>
      )}

      {/* Tech Debt: Migration Readiness */}
      {data.migrationReadiness && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Migration Readiness</h3>
          <div className={styles.scoreGrid}>
            <ScoreGauge score={data.migrationReadiness.cloudNative} label="Cloud Native" />
            <ScoreGauge score={data.migrationReadiness.containerization} label="Containerization" />
            <ScoreGauge score={data.migrationReadiness.serverless} label="Serverless" />
            <ScoreGauge score={data.migrationReadiness.frameworkUpgrade} label="Framework Upgrade" />
            <ScoreGauge score={data.migrationReadiness.overallReadiness} label="Overall Readiness" />
          </div>
          {data.migrationReadiness.blockers?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className={styles.scoreName}>Blockers</div>
              <ul className={styles.recommendationList}>
                {data.migrationReadiness.blockers.map((b: string, i: number) => <li key={i} className={styles.recommendationItem}>{b}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* API Endpoints table */}
      {data.endpoints?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Endpoints ({data.endpoints.length})</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Method</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Path</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Auth</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Validation</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>File</th>
                </tr>
              </thead>
              <tbody>
                {data.endpoints.slice(0, 30).map((ep: any, i: number) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600, color: 'var(--primary-500)' }}>{ep.method}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--font-code)', fontSize: '0.75rem' }}>{ep.path}</td>
                    <td style={{ padding: '4px 8px' }}>{ep.hasAuth ? '✓' : '✗'}</td>
                    <td style={{ padding: '4px 8px' }}>{ep.hasValidation ? '✓' : '✗'}</td>
                    <td style={{ padding: '4px 8px', fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{ep.file}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Database list */}
      {data.databases?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Detected Databases</h3>
          <div className={styles.patternGrid}>
            {data.databases.map((db: any, i: number) => (
              <div key={i} className={styles.patternCard}>
                <div className={styles.patternName}>{db.type}</div>
                <div className={styles.patternDescription}>Connection: {db.connectionPattern}{db.orm ? ` (${db.orm})` : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues / Vulnerabilities / Patterns — generic list rendering */}
      {data.vulnerabilities?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Vulnerabilities ({data.vulnerabilities.length})</h3>
          <IssueList items={data.vulnerabilities} />
        </div>
      )}
      {data.hardcodedSecrets?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Hardcoded Secrets ({data.hardcodedSecrets.length})</h3>
          <IssueList items={data.hardcodedSecrets} />
        </div>
      )}
      {data.insecurePatterns?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Insecure Patterns ({data.insecurePatterns.length})</h3>
          <IssueList items={data.insecurePatterns} nameKey="name" />
        </div>
      )}
      {data.outdatedPackages?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Outdated Packages ({data.outdatedPackages.length})</h3>
          <div className={styles.patternGrid}>
            {data.outdatedPackages.map((p: any, i: number) => (
              <div key={i} className={styles.patternCard}>
                <div className={styles.patternName}>{p.name}</div>
                <div className={styles.patternDescription}>
                  {p.currentVersion} → {p.latestVersion}
                  <SeverityBadge severity={p.semverJump} />
                  {p.breakingChanges && <span style={{ color: 'var(--color-error)', fontSize: '0.7rem', marginLeft: 6 }}>⚠ Breaking</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.issues?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Performance Issues ({data.issues.length})</h3>
          <IssueList items={data.issues} />
        </div>
      )}
      {data.errorPatterns?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Error Patterns ({data.errorPatterns.length})</h3>
          <IssueList items={data.errorPatterns} />
        </div>
      )}
      {data.missingTests?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Missing Test Areas ({data.missingTests.length})</h3>
          <IssueList items={data.missingTests} nameKey="area" />
        </div>
      )}
      {data.consistencyIssues?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Consistency Issues ({data.consistencyIssues.length})</h3>
          <IssueList items={data.consistencyIssues} nameKey="issue" />
        </div>
      )}
      {data.hotspots?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Tech Debt Hotspots</h3>
          <div className={styles.patternGrid}>
            {data.hotspots.map((h: any, i: number) => (
              <div key={i} className={styles.patternCard}>
                <div className={styles.patternName} style={{ fontFamily: 'var(--font-code)', fontSize: '0.75rem' }}>{h.file}</div>
                <div className={styles.patternDescription}>Score: {h.score}/100 · Effort: {h.estimatedEffort}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{h.reasons?.join(', ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.debtItems?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Debt Items ({data.debtItems.length})</h3>
          <IssueList items={data.debtItems} nameKey="title" />
        </div>
      )}
      {data.refactoringPriority?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Refactoring Priority</h3>
          <div className={styles.patternGrid}>
            {data.refactoringPriority.map((r: any, i: number) => (
              <div key={i} className={styles.patternCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary-500)' }}>P{r.priority}</span>
                  <span className={styles.patternName}>{r.title}</span>
                </div>
                <p className={styles.patternDescription}>{r.description}</p>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                  Effort: {r.effort} · Impact: {r.impact} · {r.category}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations?.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Recommendations</h3>
          <RecommendationsList items={data.recommendations} />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

type TabId = 'overview' | 'architecture' | 'patterns' | 'features' | 'microservices' | 'docs' | 'security' | 'dependencies' | 'performance' | 'test-coverage' | 'api-contracts' | 'database' | 'devops' | 'error-handling' | 'tech-debt' | 'ask' | 'code-dna' | 'ai-artifacts' | 'gen-docs' | 'gen-tests' | 'sdlc-ticket';

/** Confluence Space type for the export modal */
interface ConfluenceSpace {
  key: string;
  name: string;
}

/** Confluence Export Result */
interface ConfluenceExportResult {
  parentPage: { id: string; title: string; url: string };
  childPages: Array<{ dimension: string; id: string; title: string; url: string }>;
  totalPages: number;
}

/** Confluence Export Modal — choose space, create hierarchical pages */
const ConfluenceExportModal: React.FC<{
  open: boolean;
  projectId: string;
  projectName: string;
  onClose: () => void;
}> = ({ open, projectId, projectName, onClose }) => {
  const [spaces, setSpaces] = useState<ConfluenceSpace[]>([]);
  const [selectedSpace, setSelectedSpace] = useState('');
  const [parentTitle, setParentTitle] = useState(`${projectName} — Analysis Report`);
  const [loading, setLoading] = useState(false);
  const [fetchingSpaces, setFetchingSpaces] = useState(false);
  const [result, setResult] = useState<ConfluenceExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      setFetchingSpaces(true);
      brownfieldApi.getConfluenceSpaces()
        .then((res) => {
          if (res.success && res.data) {
            setSpaces(res.data);
            if (res.data.length > 0) setSelectedSpace(res.data[0].key);
          }
        })
        .catch(() => setError('Failed to load Confluence spaces. Is Confluence connected?'))
        .finally(() => setFetchingSpaces(false));
    }
  }, [open]);

  const handleExport = async () => {
    if (!selectedSpace || !parentTitle.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await brownfieldApi.exportToConfluence(projectId, selectedSpace, parentTitle.trim());
      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setError(res.error || 'Export failed');
      }
    } catch (err) {
      setError('Export to Confluence failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.progressOverlay} onClick={onClose}>
      <div className={styles.progressCard} style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: '1.125rem', fontWeight: 600 }}>📄 Export to Confluence</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
          Creates a parent page with child pages for each analysis dimension.
        </p>

        {result ? (
          /* Success state */
          <div>
            <div style={{
              padding: 16, borderRadius: 'var(--radius-lg)', background: 'rgba(16, 185, 129, 0.06)',
              border: '1px solid var(--color-success)', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--color-success)', marginBottom: 8, fontSize: '0.9rem' }}>
                ✓ Exported {result.totalPages} pages to Confluence
              </div>
              <div style={{ fontSize: '0.8rem', marginBottom: 8 }}>
                <strong>Parent Page:</strong>{' '}
                <a href={result.parentPage.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)' }}>
                  {result.parentPage.title}
                </a>
              </div>
              <div style={{ fontSize: '0.8rem' }}>
                <strong>Child Pages:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  {result.childPages.map((cp) => (
                    <li key={cp.id} style={{ marginBottom: 2 }}>
                      <a href={cp.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)', fontSize: '0.75rem' }}>
                        {cp.title}
                      </a>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginLeft: 6, textTransform: 'capitalize' }}>
                        ({cp.dimension.replace(/-/g, ' ')})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className={styles.createButton} onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          /* Form state */
          <div className={styles.createForm}>
            {error && (
              <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem' }}>
                {error}
              </div>
            )}

            <div className={styles.formField}>
              <label>Confluence Space *</label>
              {fetchingSpaces ? (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Loading spaces...</p>
              ) : spaces.length === 0 ? (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-warning)' }}>
                  No spaces found. Make sure Confluence is connected in Integrations.
                </p>
              ) : (
                <select value={selectedSpace} onChange={(e) => setSelectedSpace(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}>
                  {spaces.map((s) => (
                    <option key={s.key} value={s.key}>{s.name} ({s.key})</option>
                  ))}
                </select>
              )}
            </div>

            <div className={styles.formField}>
              <label>Parent Page Title *</label>
              <input value={parentTitle} onChange={(e) => setParentTitle(e.target.value)} placeholder="Analysis Report" />
            </div>

            {/* Page hierarchy preview */}
            <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', fontSize: '0.8rem' }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>Page Hierarchy Preview</div>
              <div style={{ paddingLeft: 0, color: 'var(--text-secondary)' }}>
                📄 {parentTitle || 'Parent Page'}
                <div style={{ paddingLeft: 20, marginTop: 4 }}>
                  <div>├ 🏗️ Architecture Analysis</div>
                  <div>├ 🧩 Design Patterns</div>
                  <div>├ 📐 Code Principles (SOLID/DRY/KISS)</div>
                  <div>├  Feature Documentation</div>
                  <div>├ 🔗 Microservice Map</div>
                  <div>├ 📝 Documentation Gaps</div>
                  <div>├  Security &amp; Vulnerability</div>
                  <div>├  Dependency &amp; Supply Chain</div>
                  <div>├  Performance &amp; Scalability</div>
                  <div>├  Test Coverage &amp; Quality</div>
                  <div>├  API Contracts</div>
                  <div>├  Database &amp; Data Model</div>
                  <div>├ 🚀 DevOps &amp; CI/CD Maturity</div>
                  <div>├ 🛡️ Error Handling &amp; Observability</div>
                  <div>└ 💰 Tech Debt Estimation</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className={styles.backButton} onClick={onClose}>Cancel</button>
              <button
                className={styles.createButton}
                disabled={!selectedSpace || !parentTitle.trim() || loading}
                onClick={handleExport}
              >
                {loading ? 'Exporting...' : '📄 Export to Confluence'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const BrownfieldPage: React.FC = () => {
  // List state
  const [projects, setProjects] = useState<BrownfieldProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Detail state
  const [selectedProject, setSelectedProject] = useState<BrownfieldProject | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [analyzing, setAnalyzing] = useState(false);
  const [showConfluenceExport, setShowConfluenceExport] = useState(false);

  // Load projects
  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await brownfieldApi.getProjects();
      if (res.success && res.data) {
        setProjects(res.data);
      }
    } catch (err) {
      console.error('Failed to load brownfield projects:', err);
      setError('Failed to load projects.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Select a project → load full details
  const selectProject = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await brownfieldApi.getProject(id);
      if (res.success && res.data) {
        setSelectedProject(res.data);
        setActiveTab('overview');
      }
    } catch {
      setError('Failed to load project details.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Create project
  const handleCreate = async (data: CreateBrownfieldProjectRequest) => {
    const res = await brownfieldApi.createProject(data);
    if (res.success && res.data) {
      await loadProjects();
      selectProject(res.data.id);
    }
  };

  // Delete project
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this project and all analysis data?')) return;
    await brownfieldApi.deleteProject(id);
    if (selectedProject?.id === id) setSelectedProject(null);
    loadProjects();
  };

  // Analysis callbacks
  const handleAnalysisComplete = useCallback(() => {
    setAnalyzing(false);
    if (selectedProject) selectProject(selectedProject.id);
  }, [selectedProject, selectProject]);

  const handleAnalysisError = useCallback((msg: string) => {
    setAnalyzing(false);
    setError(msg);
  }, []);

  const tabGroups = [
    {
      title: 'Dashboard',
      tabs: [
        { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
        { id: 'ask', label: 'Ask Codebase', icon: <MessageSquare size={18} /> },
      ],
    },
    {
      title: 'Code Intelligence',
      tabs: [
        { id: 'code-dna', label: 'Code DNA', icon: <Dna size={18} /> },
        { id: 'ai-artifacts', label: 'AI Artifacts', icon: <Bot size={18} /> },
        { id: 'gen-docs', label: 'Gen Docs', icon: <BookOpen size={18} /> },
        { id: 'gen-tests', label: 'Gen Tests', icon: <FlaskConical size={18} /> },
        { id: 'sdlc-ticket', label: 'SDLC Ticket', icon: <Ticket size={18} /> },
      ],
    },
    {
      title: 'Architecture & Design',
      tabs: [
        { id: 'architecture', label: 'Architecture', icon: <Network size={18} /> },
        { id: 'patterns', label: 'Patterns & Principles', icon: <Blocks size={18} /> },
        { id: 'microservices', label: 'Microservices', icon: <Server size={18} /> },
        { id: 'database', label: 'Database', icon: <Database size={18} />, count: selectedProject?.databaseAnalysis?.databases?.length },
        { id: 'api-contracts', label: 'API Contracts', icon: <Plug size={18} />, count: selectedProject?.apiContractAnalysis?.endpoints?.length },
      ],
    },
    {
      title: 'Quality & Operations',
      tabs: [
        { id: 'features', label: 'Features', icon: <List size={18} />, count: selectedProject?.features?.length },
        { id: 'docs', label: 'Doc Gaps', icon: <FileQuestion size={18} />, count: selectedProject?.documentationGaps?.gaps?.length },
        { id: 'security', label: 'Security', icon: <ShieldCheck size={18} />, count: selectedProject?.securityAnalysis?.vulnerabilities?.length },
        { id: 'dependencies', label: 'Dependencies', icon: <PackageOpen size={18} />, count: selectedProject?.dependencyAnalysis?.outdatedPackages?.length },
        { id: 'performance', label: 'Performance', icon: <Zap size={18} />, count: selectedProject?.performanceAnalysis?.issues?.length },
        { id: 'test-coverage', label: 'Tests', icon: <TestTube2 size={18} />, count: selectedProject?.testCoverageAnalysis?.missingTests?.length },
        { id: 'devops', label: 'DevOps', icon: <Rocket size={18} /> },
        { id: 'error-handling', label: 'Error Handling', icon: <AlertTriangle size={18} />, count: selectedProject?.errorHandlingAnalysis?.errorPatterns?.length },
        { id: 'tech-debt', label: 'Tech Debt', icon: <Coins size={18} />, count: selectedProject?.techDebtAnalysis?.debtItems?.length },
      ],
    },
  ];

  // ==================== RENDER ====================

  return (
    <AppLayout>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>App Intelligence</h1>
            <p className={styles.subtitle}>
              Analyze brownfield applications — discover architecture, patterns, principles, and generate per-feature documentation
            </p>
          </div>
        </div>

        {/* Detail View */}
        {selectedProject ? (
          <>
            <div className={styles.detailHeaderWrapper}>
              <div className={styles.detailHeader}>
                <button className={styles.backButton} onClick={() => setSelectedProject(null)}>
                  ← Back
                </button>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{selectedProject.name}</h2>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {selectedProject.repositories.map((r) => r.fullName).join(', ')}
                  </p>
                </div>
                <div className={styles.headerActions}>
                  {selectedProject.status !== 'analyzing' && !analyzing && (
                    <button className={styles.createButton} onClick={() => setAnalyzing(true)}>
                      ▶ {selectedProject.status === 'created' ? 'Run Analysis' : 'Re-Analyze'}
                    </button>
                  )}
                  {selectedProject.status === 'completed' && (
                    <button
                      className={styles.confluenceButton}
                      onClick={() => setShowConfluenceExport(true)}
                    >
                      📄 Export to Confluence
                    </button>
                  )}
                  <button className={styles.backButton} onClick={() => handleDelete(selectedProject.id)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar & Tab Content Layout */}
            <div className={styles.detailLayout}>
              {/* Sidebar */}
              <div className={styles.sidebar}>
                {tabGroups.map((group, gIdx) => (
                  <div key={gIdx} className={styles.tabGroup}>
                    <div className={styles.tabGroupHeader}>{group.title}</div>
                    <div className={styles.tabGroupItems}>
                      {group.tabs.map((t) => (
                        <button
                          key={t.id}
                          className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
                          onClick={() => setActiveTab(t.id as TabId)}
                        >
                          <div className={styles.tabLeft}><span className={styles.tabIcon}>{t.icon}</span><span className={styles.tabLabel}>{t.label}</span></div>
                          {t.count !== undefined && t.count > 0 && (
                            <span className={styles.tabBadge}>{t.count}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tab Content */}
              <div className={styles.detailContent}>
                {activeTab === 'overview' && <OverviewTab project={selectedProject} />}
                {activeTab === 'architecture' && <ArchitectureTab project={selectedProject} />}
                {activeTab === 'patterns' && <PatternsTab project={selectedProject} />}
                {activeTab === 'features' && <FeaturesTab project={selectedProject} />}
                {activeTab === 'microservices' && <MicroservicesTab project={selectedProject} />}
                {activeTab === 'docs' && <DocsGapsTab project={selectedProject} />}
                {activeTab === 'security' && <GenericDimensionTab project={selectedProject} dimension="security" />}
                {activeTab === 'dependencies' && <GenericDimensionTab project={selectedProject} dimension="dependencies" />}
                {activeTab === 'performance' && <GenericDimensionTab project={selectedProject} dimension="performance" />}
                {activeTab === 'test-coverage' && <GenericDimensionTab project={selectedProject} dimension="test-coverage" />}
                {activeTab === 'api-contracts' && <GenericDimensionTab project={selectedProject} dimension="api-contracts" />}
                {activeTab === 'database' && <GenericDimensionTab project={selectedProject} dimension="database" />}
                {activeTab === 'devops' && <GenericDimensionTab project={selectedProject} dimension="devops-maturity" />}
                {activeTab === 'error-handling' && <GenericDimensionTab project={selectedProject} dimension="error-handling" />}
                {activeTab === 'tech-debt' && <GenericDimensionTab project={selectedProject} dimension="tech-debt" />}
                {activeTab === 'ask' && <AskTab project={selectedProject} />}
                {activeTab === 'code-dna' && <CodeDNATab project={selectedProject} />}
                {activeTab === 'ai-artifacts' && <AIArtifactsTab project={selectedProject} />}
                {activeTab === 'gen-docs' && <GeneratedDocsTab project={selectedProject} />}
                {activeTab === 'gen-tests' && <GeneratedTestsTab project={selectedProject} />}
                {activeTab === 'sdlc-ticket' && <TicketProcessingTab project={selectedProject} />}
              </div>
            </div>

            {/* Analysis Progress Overlay */}
            {analyzing && (
              <AnalysisProgressPanel
                projectId={selectedProject.id}
                onComplete={handleAnalysisComplete}
                onError={handleAnalysisError}
              />
            )}
          </>
        ) : (
          <>
            {/* Stats Bar */}
            {!isLoading && projects.length > 0 && (
              <div className={styles.statsBar}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>{projects.length}</div>
                  <div className={styles.statLabel}>Projects</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>
                    {projects.filter((p) => p.status === 'completed').length}
                  </div>
                  <div className={styles.statLabel}>Analyzed</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>
                    {projects.reduce((sum, p) => sum + p.repositoryCount, 0)}
                  </div>
                  <div className={styles.statLabel}>Repositories</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>
                    {Math.round(
                      projects.filter((p) => p.healthScore !== null).reduce((sum, p) => sum + (p.healthScore ?? 0), 0) /
                        (projects.filter((p) => p.healthScore !== null).length || 1)
                    )}
                  </div>
                  <div className={styles.statLabel}>Avg Health</div>
                </div>
              </div>
            )}

            {/* Project List */}
            <div className={styles.content}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: '1.125rem' }}>Projects</h2>
                <button className={styles.createButton} onClick={() => setShowCreateModal(true)}>
                  + New Project
                </button>
              </div>

              {isLoading ? (
                <div className={styles.loadingState}>Loading projects...</div>
              ) : error ? (
                <div className={styles.errorState}>
                  <p>{error}</p>
                  <button onClick={loadProjects}>Retry</button>
                </div>
              ) : projects.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>🔍</div>
                  <h3 className={styles.emptyTitle}>No Projects Yet</h3>
                  <p className={styles.emptyDescription}>
                    Connect your GitHub repositories to analyze legacy applications and generate comprehensive documentation.
                  </p>
                  <button className={styles.createButton} onClick={() => setShowCreateModal(true)}>
                    + Create First Project
                  </button>
                </div>
              ) : (
                <div className={styles.projectGrid}>
                  {projects.map((p) => (
                    <ProjectCard
                      key={p.id}
                      project={p}
                      onSelect={selectProject}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Create Modal */}
        <CreateProjectModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />

        {/* Confluence Export Modal */}
        {selectedProject && (
          <ConfluenceExportModal
            open={showConfluenceExport}
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            onClose={() => setShowConfluenceExport(false)}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default BrownfieldPage;
